'use client';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { cn, createTestIdHelper } from '../../lib/utils';

import { buttonVariants, type ButtonProps } from './button';

const testId = createTestIdHelper('confirm-popover');

// Internal wiring context: supplies the ids that link the alertdialog aria refs
// to the Title/Description slots. Generic id plumbing only — no feature data.
interface ConfirmPopoverContextValue {
  titleId: string;
  descriptionId: string;
}

const ConfirmPopoverContext =
  React.createContext<ConfirmPopoverContextValue | null>(null);

function useConfirmPopoverContext() {
  const ctx = React.useContext(ConfirmPopoverContext);
  if (!ctx) {
    throw new Error('ConfirmPopover.* must be used within <ConfirmPopover>');
  }
  return ctx;
}

function ConfirmPopover({
  children,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const value = React.useMemo(
    () => ({ titleId, descriptionId }),
    [titleId, descriptionId],
  );
  return (
    <ConfirmPopoverContext.Provider value={value}>
      <PopoverPrimitive.Root {...props}>{children}</PopoverPrimitive.Root>
    </ConfirmPopoverContext.Provider>
  );
}

function ConfirmPopoverTrigger(
  props: React.ComponentProps<typeof PopoverPrimitive.Trigger>,
) {
  return (
    <PopoverPrimitive.Trigger data-slot="confirm-popover-trigger" {...props} />
  );
}

function ConfirmPopoverContent({
  className,
  align = 'end',
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  const { titleId, descriptionId } = useConfirmPopoverContext();
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="confirm-popover-content"
        role="alertdialog"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        align={align}
        sideOffset={sideOffset}
        style={{ zIndex: 99999 }} // matches popover.tsx portal stacking
        className={cn(
          // shadow-overlay (not shadow-md): the app defines --shadow-overlay as
          // the popover/dropdown elevation, whereas the --shadow-sm/md/lg vars
          // the default Tailwind shadow scale maps to are undefined here and
          // render as no shadow, leaving the overlay reading flat against the
          // content beneath it.
          'w-64 rounded-md border bg-popover p-3 text-popover-foreground shadow-overlay outline-none',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function ConfirmPopoverHeader({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="confirm-popover-header"
      className={cn('flex flex-col gap-1', className)}
      {...props}
    />
  );
}

function ConfirmPopoverTitle({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  const { titleId } = useConfirmPopoverContext();
  return (
    <p
      id={titleId}
      data-slot="confirm-popover-title"
      className={cn('text-sm font-medium text-foreground', className)}
      {...props}
    />
  );
}

function ConfirmPopoverDescription({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  const { descriptionId } = useConfirmPopoverContext();
  return (
    <p
      id={descriptionId}
      data-slot="confirm-popover-description"
      // Full-ink (not muted): this is the consequence line of a confirm, often
      // destructive. `text-muted-foreground` renders ~2.5:1 on the popover
      // surface, below the WCAG AA floor; hierarchy vs the title is carried by
      // size + weight (title is font-medium), not by lightening this text.
      className={cn('text-xs text-foreground', className)}
      {...props}
    />
  );
}

function ConfirmPopoverFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="confirm-popover-footer"
      className={cn('mt-3 flex justify-end gap-2', className)}
      {...props}
    />
  );
}

// Renders on PopoverPrimitive.Close → dismisses natively. Kept first in DOM so
// Radix auto-focus lands here — the non-committal choice gets focus by default.
function ConfirmPopoverCancel({
  className,
  variant = 'ghost',
  size = 'xs',
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Close> &
  Pick<ButtonProps, 'variant' | 'size'>) {
  return (
    <PopoverPrimitive.Close
      data-slot="confirm-popover-cancel"
      data-testid={testId('cancel')}
      // text-xs (fixed, incl. fhd): the generic Button size scale keeps text-sm
      // regardless of size, so an xs button in this compact 256px popover renders
      // at the title's size and above the description's — the quietest element
      // reading loudest. Pin the label to the popover's own fixed scale (its
      // title/description don't bump at fhd either) so hierarchy stays title >
      // body ≈ actions. Caller className still wins (merged last).
      className={cn(
        buttonVariants({ variant, size }),
        'text-xs fhd:text-xs',
        className,
      )}
      {...props} // caller may override data-testid to preserve feature selectors
    />
  );
}

// Fires the caller's onClick AND closes (Radix Close). Neutral (primary) by
// default so the component stays agnostic; a destructive call site opts in with
// `variant="destructive"`. The label is caller-owned via children.
function ConfirmPopoverAction({
  className,
  variant = 'default',
  size = 'xs',
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Close> &
  Pick<ButtonProps, 'variant' | 'size'>) {
  return (
    <PopoverPrimitive.Close
      data-slot="confirm-popover-action"
      data-testid={testId('action')}
      // See ConfirmPopoverCancel: pin the label to the popover's fixed scale.
      className={cn(
        buttonVariants({ variant, size }),
        'text-xs fhd:text-xs',
        className,
      )}
      {...props}
    />
  );
}

export {
  ConfirmPopover,
  ConfirmPopoverTrigger,
  ConfirmPopoverContent,
  ConfirmPopoverHeader,
  ConfirmPopoverTitle,
  ConfirmPopoverDescription,
  ConfirmPopoverFooter,
  ConfirmPopoverCancel,
  ConfirmPopoverAction,
};
